import React, { lazy, Suspense, memo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useResolvedPath } from "react-router-dom";
import Loading from "components/shared-components/Loading";
import useUserPermissions from "tools/validateAccess";

import Views from "views";

const AppLayout = lazy(() => import("./AppLayout"));
const AuthLayout = lazy(() => import("./AuthLayout"));
const ExternalLayout = lazy(() => import("./ExternalLayout"));

const Layouts = () => {
  const { pathname } = useResolvedPath();
  const token = useSelector((state) => state.auth.token);
  const blankLayout = useSelector((state) => state.theme.blankLayout);
  const [permissionsReady, setPermissionsReady] = useState(false);

  // Hook para validar permisos
  const { getUserPermissionType } = useUserPermissions();

  useEffect(() => {
    let isMounted = true;
    let retryTimeout;

    const checkPermissions = () => {
      const currentPermissions = localStorage.getItem("userPermissions");
      
      if (currentPermissions) {
        try {
          const parsed = JSON.parse(currentPermissions);
          if (parsed.permissions && Array.isArray(parsed.permissions) && parsed.permissions.length > 0) {
            if (isMounted) {
              setPermissionsReady(true);
            }
            return;
          }
        } catch (e) {
          console.error("Error al parsear permisos:", e);
        }
      }

      // Si no hay permisos, reintentamos cada 3 segundos
      retryTimeout = setTimeout(checkPermissions, 3000);
    };

    // Solo verificamos permisos si hay un token
    if (token) {
      checkPermissions();
    } else {
      setPermissionsReady(true); // Si no hay token, no necesitamos esperar permisos
    }

    return () => {
      isMounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [token]);

  // Verificar si la ruta es "auth/no-access-permission"
  if (pathname === "/auth/no-access-permission") {
    return (
      <Suspense fallback={<Loading cover="content" />}>
        <Views />
      </Suspense>
    );
  }

  // Si hay token pero los permisos no están listos, mostramos el spinner
  if (token && !permissionsReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <Loading cover="content" />
      </div>
    );
  }

  let Layout;
  if (pathname.startsWith("/external")) {
    Layout = ExternalLayout;
  } else if (token && !blankLayout) {
    Layout = AppLayout;
  } else {
    Layout = AuthLayout;
  }

  return (
    <Suspense fallback={<Loading cover="content" />}>
      <Layout>
        <Views />
      </Layout>
    </Suspense>
  );
};

export default memo(Layouts);
